/**
 * Tipos e constantes do domínio "miolo de agenda".
 * Tudo em português do Brasil para casar com a linguagem do produto.
 */
/* ------------------------------------------------------------------ */
/* Formatos de papel                                                   */
/* ------------------------------------------------------------------ */
/**
 * Formatos de folha.
 *
 * A4, A5 e A6 são os três clássicos; os outros quatro são medidas de papelaria
 * (Slim, Quadrado, Big e Universitário), todas com margem, grade e corpo de
 * texto próprios — declarados em `PAPEIS`, não decididos por nome de formato.
 */
export const FORMATOS = ['A4', 'A5', 'A6', 'SLIM', 'QUADRADO', 'BIG', 'UNIVERSITARIO'];
/** Vertical (retrato) ou horizontal (paisagem). */
export const ORIENTACOES = ['RETRATO', 'PAISAGEM'];
export const ROTULO_ORIENTACAO = {
    RETRATO: 'Vertical (retrato)',
    PAISAGEM: 'Horizontal (paisagem)',
};
export const DESCRICAO_ORIENTACAO = {
    RETRATO: 'Como um caderno comum: mais alto que largo. Ideal para agenda de dia.',
    PAISAGEM: 'Deitado: mais largo que alto. Bom para planejamento semanal, mapas e painéis.',
};
/**
 * Onde fica a margem de encadernação.
 *
 * - ESPELHADA: alterna entre as páginas, como num livro (padrão);
 * - ESQUERDA / DIREITA: sempre do mesmo lado — espiral ou fichário;
 * - TOPO: margem em cima, para bloco de notas;
 * - NENHUMA: margens simétricas, para página solta ou quadro.
 */
export const LADOS_LOMBADA = ['ESPELHADA', 'ESQUERDA', 'DIREITA', 'TOPO', 'NENHUMA'];
/**
 * Como o miolo vai ser encadernado.
 *
 * Serve para desenhar a encadernação na prévia e, quando o cliente pede, para
 * imprimir a guia de furo na margem. Quem encaderna é a gráfica: a escolha aqui
 * não muda o conteúdo, só a representação e a margem sugerida.
 */
/** Sangria sugerida para gráfica, em milímetros. */
export const SANGRIA_DE_CORTE_MM = 2;
export const ROTULO_SANGRIA = 'Sangria de corte (2 mm)';
export const DESCRICAO_SANGRIA = 'Para gráfica: a folha cresce 2 mm de cada lado, o fundo acompanha e as marcas de corte mostram onde a tesoura entra. Desligada, o arquivo sai no tamanho exato da folha.';
export const ENCADERNACOES = ['NENHUMA', 'WIRE_O', 'ESPIRAL', 'CAPA_DURA'];
export const ROTULO_ENCADERNACAO = {
    NENHUMA: 'Sem encadernação desenhada',
    WIRE_O: 'Wire-o (arame duplo)',
    ESPIRAL: 'Espiral plástico',
    CAPA_DURA: 'Capa dura (lombada larga)',
};
export const DESCRICAO_ENCADERNACAO = {
    NENHUMA: 'A prévia mostra a folha solta.',
    WIRE_O: 'Furo de 3:1 (passo de 8,5 mm): o arame atravessa a margem da lombada.',
    ESPIRAL: 'O plástico enrosca na margem da lombada, com o mesmo passo do wire-o.',
    CAPA_DURA: 'A lombada larga come de 15 a 20 mm do lado da encadernação.',
};
/**
 * Geometria do furo do wire-o, em milímetros.
 *
 * O padrão de mercado é 3:1 — três furos por polegada, ou 8,47 mm de passo — a
 * 6 mm da borda. Um miolo mais fino aceita 2:1 (12,7 mm), que é o que se usa em
 * agendas grossas.
 */
export const PASSO_FURO_WIRE_O_MM = 8.47;
export const PASSO_FURO_2_1_MM = 12.7;
/** Distância do centro do furo até a borda da folha. */
export const FURO_ATE_A_BORDA_MM = 6;
/**
 * Folga nas pontas: o primeiro e o último furo ficam a esta distância das
 * bordas, senão a folha rasga no manuseio.
 */
export const MARGEM_DO_FURO_MM = 12;
/**
 * Centros dos furos ao longo da borda, em milímetros.
 *
 * É a geometria real do furo 3:1 (três por polegada): o primeiro centro a 12 mm
 * da ponta e os demais a cada 8,47 mm. A guia impressa e o desenho da prévia
 * usam esta mesma conta — se as duas divergirem, a encadernadora fura fora da
 * marca.
 */
export function posicoesDosFuros(comprimentoMm, passoMm = PASSO_FURO_WIRE_O_MM) {
    const centros = [];
    for (let centro = MARGEM_DO_FURO_MM; centro + MARGEM_DO_FURO_MM <= comprimentoMm + 0.01; centro += passoMm) {
        centros.push(Number(centro.toFixed(2)));
    }
    return centros;
}
/** Quantos furos cabem na borda da folha. */
export function furosDeWireO(comprimentoMm, passoMm = PASSO_FURO_WIRE_O_MM) {
    return posicoesDosFuros(comprimentoMm, passoMm).length;
}
export const ROTULO_LOMBADA = {
    ESPELHADA: 'Espelhada (livro)',
    ESQUERDA: 'Sempre à esquerda',
    DIREITA: 'Sempre à direita',
    TOPO: 'No topo (bloco de notas)',
    NENHUMA: 'Sem margem extra',
};
export const DESCRICAO_LOMBADA = {
    ESPELHADA: 'A margem maior troca de lado nas páginas pares, como num livro grampeado.',
    ESQUERDA: 'Toda a margem de encadernação fica à esquerda — espiral, fichário.',
    DIREITA: 'Toda a margem de encadernação fica à direita.',
    TOPO: 'A margem extra vai para o topo, como em bloco de notas com cola em cima.',
    NENHUMA: 'Margens iguais dos dois lados: a folha fica livre para imprimir e vender solta.',
};
/* ------------------------------------------------------------------ */
/* Dias por página                                                     */
/* ------------------------------------------------------------------ */
export const DIAS_POR_PAGINA = [1, 2];
/**
 * Como as páginas de dia são montadas.
 *
 * - `DIA`: um ou dois dias por página, como numa agenda comum;
 * - `SEMANA`: os sete dias da semana na mesma página, cada um com o seu espaço
 *   de escrita — o formato “semana” dos planejadores;
 * - `NENHUM`: o miolo não tem páginas de dia nenhuma, então serve para montar
 *   um caderno só com folhas para escrever.
 */
export const MODOS_DIA = ['DIA', 'SEMANA', 'NENHUM'];
export const ROTULO_MODO_DIA = {
    DIA: 'Dia por dia',
    SEMANA: 'Semana na mesma página',
    NENHUM: 'Sem páginas de dia',
};
export const DESCRICAO_MODO_DIA = {
    DIA: 'Uma data por folha (ou duas), com todo o espaço de escrita do dia.',
    SEMANA: 'Segunda a domingo na mesma página, cada dia com o seu espaço de escrita.',
    NENHUM: 'Nenhuma página de dia: o miolo fica só com as páginas extras e os cadernos.',
};
/**
 * Fim de semana dividido: sábado e domingo na mesma folha.
 *
 * Vale com **um dia por página** — de segunda a sexta cada dia fica com a folha
 * inteira, e o sábado leva o domingo junto, cada um com metade da altura.
 * Desligado (ou ausente, nos miolos salvos antes deste campo), o miolo sai
 * exatamente como sempre saiu.
 */
export const ROTULO_FIM_DE_SEMANA_DIVIDIDO = 'Fim de semana dividido';
export const DESCRICAO_FIM_DE_SEMANA_DIVIDIDO = 'Sábado e domingo dividem a mesma folha, cada um com metade da altura. Os dias de semana continuam com a folha inteira.';
/* ------------------------------------------------------------------ */
/* Quais dias da semana entram na agenda                               */
/* ------------------------------------------------------------------ */
export const SEMANAS = ['SEG_A_SEX', 'SEG_A_SAB', 'SEG_A_DOM', 'SAB_E_DOM'];
export const ROTULO_SEMANA = {
    SEG_A_SEX: 'Segunda a sexta',
    SEG_A_SAB: 'Segunda a sábado',
    SEG_A_DOM: 'Segunda a domingo',
    SAB_E_DOM: 'Sábado e domingo',
};
export const DESCRICAO_SEMANA = {
    SEG_A_SEX: 'Foco em trabalho e rotina. Ideal para quem usa o fim de semana para descansar.',
    SEG_A_SAB: 'Inclui o sábado para quem trabalha ou estuda aos sábados.',
    SEG_A_DOM: 'Agenda completa, com os 7 dias da semana.',
    SAB_E_DOM: 'Diário de fim de semana, para registro pessoal e lazer.',
};
/* ------------------------------------------------------------------ */
/* Teoria das cores                                                    */
/* ------------------------------------------------------------------ */
export const ESQUEMAS_HARMONIA = [
    'AUTO',
    'MONOCROMATICO',
    'ANALOGO',
    'COMPLEMENTAR',
    'COMPLEMENTAR_DIVIDIDO',
    'TRIADE',
    'TETRADE',
];
export const ROTULO_ESQUEMA = {
    AUTO: 'Deixar a IA escolher',
    MONOCROMATICO: 'Monocromático',
    ANALOGO: 'Análogo',
    COMPLEMENTAR: 'Complementar',
    COMPLEMENTAR_DIVIDIDO: 'Complementar dividido',
    TRIADE: 'Tríade',
    TETRADE: 'Tétrade',
};
/* ------------------------------------------------------------------ */
/* Estilo de conteúdo                                                  */
/* ------------------------------------------------------------------ */
export const ESTILOS = ['DEVOCIONAL', 'MOTIVACIONAL', 'CURIOSIDADES', 'PERGUNTAS', 'ESTUDAR', 'SEM_ESTILO'];
export const ROTULO_ESTILO = {
    DEVOCIONAL: 'Devocional',
    MOTIVACIONAL: 'Motivacional',
    CURIOSIDADES: 'Curiosidades',
    PERGUNTAS: 'Perguntas e diário guiado',
    ESTUDAR: 'Estudar brincando',
    SEM_ESTILO: 'Sem estilo (livre)',
};
export const DESCRICAO_ESTILO = {
    DEVOCIONAL: 'Referência bíblica, reflexão curta, espaço de oração e gratidão.',
    MOTIVACIONAL: 'Frase do dia, 3 prioridades, meta e reflexão noturna.',
    CURIOSIDADES: 'Um fato do mundo por dia, com o porquê por dentro e uma coisa para observar ou testar — de bichos e plantas a invenções do dia a dia.',
    PERGUNTAS: 'Uma pergunta por dia para pensar e um passo de escrita, com as linhas prontas — para quem quer escrever sobre a própria vida sem ficar olhando a página em branco.',
    ESTUDAR: 'Um desafio curto por dia, com a pista de como chegar lá, e uma palavra nova para usar numa frase — para fazer junto com criança, em dez minutos e sem material especial.',
    SEM_ESTILO: 'Somente a estrutura de escrita. Liberdade total para você preencher.',
};
/* ------------------------------------------------------------------ */
/* Estilo visual (acabamento)                                          */
/* ------------------------------------------------------------------ */
/**
 * Acabamento da página, independente da paleta.
 *
 * O tema decide as cores; o estilo visual decide como a página se comporta por
 * dentro: tipografia, ornamento e o tratamento dos quadros do dia. É por isso
 * que ele atravessa todos os modelos — a mesma agenda fitness sai clássica,
 * moderna ou contemporânea sem perder o conteúdo.
 */
export const ESTILOS_VISUAIS = ['CLASSICO', 'MODERNO', 'CONTEMPORANEO'];
export const ROTULO_ESTILO_VISUAL = {
    CLASSICO: 'Clássico',
    MODERNO: 'Moderno',
    CONTEMPORANEO: 'Contemporâneo',
};
export const DESCRICAO_ESTILO_VISUAL = {
    CLASSICO: 'Serifada, cantos arredondados e quadros com fundo suave. Editorial e atemporal.',
    MODERNO: 'Sem serifa, cantos discretos e quadros só com contorno. Limpo e direto.',
    CONTEMPORANEO: 'Tipografia mista, cantos retos e dia sem caixa: só o título e as linhas.',
};
/** O que cada estilo muda, item por item — mostrado no assistente. */
export const MUDANCAS_DO_ESTILO_VISUAL = {
    CLASSICO: ['Tipografia serifada', 'Ornamento de filete fino', 'Quadros com fundo e canto de 3 mm'],
    MODERNO: ['Tipografia sem serifa', 'Ornamento geométrico', 'Quadros só com contorno, canto de 1 mm'],
    CONTEMPORANEO: ['Tipografia mista', 'Sem ornamento', 'Dia sem caixa: título e linhas'],
};
/* ------------------------------------------------------------------ */
/* Acabamento das caixas do dia                                        */
/* ------------------------------------------------------------------ */
/**
 * O enfeite do título das caixas do dia.
 *
 * Os quatro são **pintura**: entram como camada por trás ou por baixo do título,
 * sem preenchimento, sem margem e sem mexer na largura em que o título é medido.
 * É o que permite oferecer mais cor sem reabrir a porta do corte em silêncio.
 */
export const ACABAMENTOS_DA_CAIXA = ['SIMPLES', 'FILETE', 'FAIXA', 'TOPO'];
export const ROTULO_ACABAMENTO_DA_CAIXA = {
    SIMPLES: 'Título solto',
    FILETE: 'Filete sob o título',
    FAIXA: 'Faixa no título',
    TOPO: 'Faixa no topo da caixa',
};
export const DESCRICAO_ACABAMENTO_DA_CAIXA = {
    SIMPLES: 'O título e nada mais, como sempre foi.',
    FILETE: 'Um fio na cor de destaque embaixo do título — discreto e clássico.',
    FAIXA: 'Uma faixa clara da cor de destaque atrás do título, como um selo.',
    TOPO: 'Uma tarja na cor de destaque no alto do quadro. Vale para os quadros que têm caixa.',
};
/**
 * Tratamento dos quadros do dia em cada estilo.
 *
 * `semCaixa` é o que atende o pedido de ter "só os dias": some o contorno, some
 * o fundo e o quadro vira título + linhas soltas na folha.
 */
export const ACABAMENTO_DO_ESTILO_VISUAL = {
    CLASSICO: {
        raioMm: 3,
        semFundo: false,
        semCaixa: false,
        ornamento: 'LINHA_FINA',
        familia: 'SERIFA',
        trackingTitulos: 0.14,
        acabamentoDaCaixa: 'FILETE',
    },
    MODERNO: {
        raioMm: 1,
        semFundo: true,
        semCaixa: false,
        ornamento: 'GEOMETRICO',
        familia: 'SANS',
        trackingTitulos: 0.16,
        acabamentoDaCaixa: 'FAIXA',
    },
    CONTEMPORANEO: {
        raioMm: 0,
        semFundo: true,
        semCaixa: true,
        ornamento: 'NENHUM',
        familia: 'MISTA',
        trackingTitulos: 0.2,
        /*
         * Sem caixa não há onde pintar a tarja do topo, então o contemporâneo fica
         * com a faixa atrás do título: ela funciona com ou sem contorno.
         */
        acabamentoDaCaixa: 'FAIXA',
    },
};
/* ------------------------------------------------------------------ */
/* Moldura da página                                                   */
/* ------------------------------------------------------------------ */
/**
 * Moldura decorativa em volta do conteúdo.
 *
 * Vale para as páginas de dia e para as divisórias de mês, que é onde a página
 * é quase toda espaço de escrita — nas páginas fechadas de conteúdo (capa,
 * índice, cadernos) a moldura competiria com o desenho e não entra.
 *
 * A cor e a espessura saem do tema, como o ornamento; o que se escolhe aqui é o
 * desenho.
 */
export const MOLDURAS = [
    'NENHUMA',
    'FILETE',
    'FILETE_DUPLO',
    'CANTOS',
    'TRACEJADA',
    'ARCO',
];
export const ROTULO_MOLDURA = {
    NENHUMA: 'Sem moldura',
    FILETE: 'Filete',
    FILETE_DUPLO: 'Filete duplo',
    CANTOS: 'Cantos',
    TRACEJADA: 'Tracejada',
    ARCO: 'Arco',
};
export const DESCRICAO_MOLDURA = {
    NENHUMA: 'A folha fica limpa, só com o cabeçalho e as linhas.',
    FILETE: 'Um contorno fino em volta de todo o conteúdo.',
    FILETE_DUPLO: 'Dois contornos finos, um dentro do outro.',
    CANTOS: 'Marcas em L nos quatro cantos, sem fechar o quadro.',
    TRACEJADA: 'Contorno pontilhado, leve, quase um guia de recorte.',
    ARCO: 'Contorno de cantos arredondados com um arco no alto.',
};
/**
 * Limites do traço da moldura.
 *
 * Abaixo de 0,2 mm a linha desaparece na impressora doméstica; acima de 1,2 mm
 * ela vira tarja e briga com o texto — e a folga de 4 mm não segura um traço
 * muito mais grosso que isso.
 */
export const ESPESSURA_MINIMA_DA_MOLDURA_MM = 0.2;
export const ESPESSURA_MAXIMA_DA_MOLDURA_MM = 1.2;
export const ESPESSURA_PADRAO_DA_MOLDURA_MM = 0.35;
/* ------------------------------------------------------------------ */
/* Calendário                                                          */
/* ------------------------------------------------------------------ */
export const MODOS_CALENDARIO = ['DATADO', 'PERMANENTE'];
/**
 * Anos que a agenda apresenta, do mais antigo ao mais novo.
 *
 * Exemplo: ano 2026 com 1 para trás e 2 para frente → 2025, 2026, 2027 e 2028.
 */
export function anosDaAgenda(calendario) {
    const entreZeroECinco = (valor) => Math.max(0, Math.min(5, Math.round(valor ?? 0)));
    const paraTras = entreZeroECinco(calendario.anosParaTras);
    const paraFrente = entreZeroECinco(calendario.anosParaFrente);
    const anos = [];
    for (let ano = calendario.ano - paraTras; ano <= calendario.ano + paraFrente; ano += 1) {
        anos.push(ano);
    }
    return anos;
}
/* ------------------------------------------------------------------ */
/* Escrita (linhas / pontilhado / grade)                               */
/* ------------------------------------------------------------------ */
export const ESTILOS_ESCRITA = ['LINHA', 'PONTILHADO', 'QUADRICULADO', 'PONTOS', 'SEM_LINHA'];
export const ROTULO_ESCRITA = {
    LINHA: 'Linhas',
    PONTILHADO: 'Linhas pontilhadas',
    QUADRICULADO: 'Quadriculado',
    PONTOS: 'Grade de pontos',
    SEM_LINHA: 'Em branco',
};
/* ---- Traço e enfeites das linhas ---- */
/**
 * Como o traço da linha é desenhado. Vale para os estilos de escrita que têm
 * linha (LINHA e PONTILHADO); com quadriculado ou grade de pontos o traço é
 * ignorado e as linhas decorativas continuam valendo.
 */
export const TRACOS_LINHA = [
    'SOLIDA',
    'TRACEJADA',
    'PONTILHADA',
    'DUPLA',
    'TRIPLA',
    'ONDULADA',
    'ZIGUE',
    'DEGRADE',
];
export const ROTULO_TRACO = {
    SOLIDA: 'Linha cheia',
    TRACEJADA: 'Tracejada',
    PONTILHADA: 'Pontilhada',
    DUPLA: 'Linha dupla',
    TRIPLA: 'Três fios',
    ONDULADA: 'Ondulada',
    ZIGUE: 'Zigue-zague',
    DEGRADE: 'Degradê',
};
export const DESCRICAO_TRACO = {
    SOLIDA: 'O traço clássico, contínuo de ponta a ponta.',
    TRACEJADA: 'Traços curtos e intervalos iguais. Leve e descontraída.',
    PONTILHADA: 'Pontinhos no lugar do traço. Boa para quem escreve com letra grande.',
    DUPLA: 'Dois fios finos, como em papel de carta.',
    TRIPLA: 'Três fios, para páginas de caligrafia e cartas.',
    ONDULADA: 'Linha em ondas suaves, romântica e infantil.',
    ZIGUE: 'Zigue-zague, com cara de caderno de esboço.',
    DEGRADE: 'O fio nasce, escurece e desaparece — efeito de rascunho.',
};
/**
 * Desenho que enfeita as linhas: aparece acima de cada linha (nos enfeites por
 * linha) ou repetido ao longo da linha, como uma faixa de demarcação.
 */
export const MOTIVOS_LINHA = [
    'NENHUM',
    'CORACAO',
    'PATINHA',
    'ESTRELA',
    'FLOR',
    'LACO',
    'FOLHA',
    'GOTA',
    'LIVRO',
    'LAPIS',
    'MALETA',
    'GRAFICO',
    'PRANCHETA',
    'BALANCA',
    'NOTA',
    'PINCEL',
    'XICARA',
    'CUPCAKE',
    'TESOURA',
    'ESMALTE',
    'DENTE',
    'CRUZ_MEDICA',
    'PULSO',
    'ASPAS',
    'ALVO',
    'LUA',
    'CRUZ',
    'CHECK',
    'TELEFONE',
    'LAMPADA',
    'CARINHA',
    'SOL',
    'MOEDA',
    'RELOGIO',
    'CALENDARIO',
    'INTERROGACAO',
    'OLHO',
];
export const ROTULO_MOTIVO = {
    NENHUM: 'Sem desenho',
    CORACAO: 'Corações',
    PATINHA: 'Patinhas de animal',
    ESTRELA: 'Estrelas',
    FLOR: 'Flores',
    LACO: 'Laços',
    FOLHA: 'Folhas',
    GOTA: 'Gotas',
    LIVRO: 'Livros',
    LAPIS: 'Lápis',
    MALETA: 'Maletas',
    GRAFICO: 'Gráficos',
    PRANCHETA: 'Pranchetas',
    BALANCA: 'Balanças',
    NOTA: 'Notas musicais',
    PINCEL: 'Pincéis',
    XICARA: 'Xícaras',
    CUPCAKE: 'Docinhos',
    TESOURA: 'Tesouras',
    ESMALTE: 'Esmaltes',
    DENTE: 'Dentinhos',
    CRUZ_MEDICA: 'Cruzes da saúde',
    PULSO: 'Batimentos',
    ASPAS: 'Aspas',
    ALVO: 'Alvos',
    LUA: 'Luas',
    CRUZ: 'Cruzes',
    CHECK: 'Vistos',
    TELEFONE: 'Telefones',
    LAMPADA: 'Lâmpadas',
    CARINHA: 'Carinhas',
    SOL: 'Sois',
    MOEDA: 'Moedas',
    RELOGIO: 'Relógios',
    CALENDARIO: 'Calendários',
    INTERROGACAO: 'Interrogações',
    OLHO: 'Olhos',
};
/** A que universo cada desenho costuma servir — usado só para agrupar na tela. */
export const GRUPO_MOTIVO = {
    NENHUM: 'Neutro',
    CORACAO: 'Amor e festas',
    PATINHA: 'Amor e festas',
    ESTRELA: 'Amor e festas',
    FLOR: 'Amor e festas',
    LACO: 'Amor e festas',
    FOLHA: 'Natureza',
    GOTA: 'Natureza',
    LIVRO: 'Estudos e escolas',
    LAPIS: 'Estudos e escolas',
    MALETA: 'Negócios e gestão',
    GRAFICO: 'Negócios e gestão',
    PRANCHETA: 'Negócios e gestão',
    BALANCA: 'Serviços e ofícios',
    NOTA: 'Arte e música',
    PINCEL: 'Arte e música',
    XICARA: 'Casa e cozinha',
    CUPCAKE: 'Casa e cozinha',
    TESOURA: 'Beleza e bem-estar',
    ESMALTE: 'Beleza e bem-estar',
    DENTE: 'Saúde',
    CRUZ_MEDICA: 'Saúde',
    PULSO: 'Saúde',
    ASPAS: 'Neutro',
    ALVO: 'Neutro',
    LUA: 'Natureza',
    CRUZ: 'Neutro',
    CHECK: 'Neutro',
    TELEFONE: 'Neutro',
    LAMPADA: 'Neutro',
    CARINHA: 'Neutro',
    SOL: 'Natureza',
    MOEDA: 'Negócios e gestão',
    RELOGIO: 'Neutro',
    CALENDARIO: 'Neutro',
    INTERROGACAO: 'Neutro',
    OLHO: 'Neutro',
};
const ORDEM_GRUPOS_MOTIVO = [
    'Neutro',
    'Amor e festas',
    'Natureza',
    'Casa e cozinha',
    'Estudos e escolas',
    'Negócios e gestão',
    'Saúde',
    'Beleza e bem-estar',
    'Arte e música',
    'Serviços e ofícios',
];
/** Motivos agrupados, na ordem em que o assistente mostra. */
export function motivosPorGrupo() {
    return ORDEM_GRUPOS_MOTIVO.map((grupo) => ({
        grupo,
        motivos: MOTIVOS_LINHA.filter((motivo) => GRUPO_MOTIVO[motivo] === grupo),
    })).filter((entrada) => entrada.motivos.length > 0);
}
/** Como o desenho se distribui nas linhas. */
export const MODOS_MOTIVO = ['AO_LONGO_DA_LINHA', 'POR_LINHA'];
export const ROTULO_MODO_MOTIVO = {
    AO_LONGO_DA_LINHA: 'Repetido ao longo da linha',
    POR_LINHA: 'Um desenho por linha, na margem',
};
export const DESCRICAO_MODO_MOTIVO = {
    AO_LONGO_DA_LINHA: 'O desenho se repete de espaço em espaço sobre a própria linha, como uma faixa de demarcação.',
    POR_LINHA: 'O desenho fica na margem, no alto de cada linha, como um marcador de escrita.',
};
export const LADOS_MOTIVO = ['ESQUERDA', 'DIREITA', 'AMBOS'];
export const ROTULO_LADO_MOTIVO = {
    ESQUERDA: 'Margem esquerda',
    DIREITA: 'Margem direita',
    AMBOS: 'As duas margens',
};
/* ------------------------------------------------------------------ */
/* Tipografia                                                          */
/* ------------------------------------------------------------------ */
export const FAMILIAS_FONTE = ['SERIFA', 'SANS', 'MONO', 'MISTA'];
export const ROTULO_FONTE = {
    SERIFA: 'Clássica (serifada)',
    SANS: 'Moderna (sem serifa)',
    MONO: 'Técnica (monoespaçada)',
    MISTA: 'Mista (títulos serifados + texto sem serifa)',
};
/**
 * Fontes que o usuário escolhe **pelo nome**.
 *
 * Os arquivos moram no próprio site (`apps/web/public/fontes/`) e viajam dentro
 * do PDF — impressão não pode depender de internet, senão a gráfica recebe
 * outra fonte e o miolo sai com a medida de outra letra.
 *
 * `SISTEMA` é a família do aparelho (Georgia e a pilha sem serifa), que é o que
 * existia antes e continua sendo o padrão de quem não escolhe.
 */
/**
 * As fontes que o usuário escolhe pelo nome.
 *
 * Todas menos `SISTEMA` são fontes do Google Fonts, servidas **pelo próprio
 * site** (`apps/web/public/fontes/`) e embutidas no PDF — escolher uma delas não
 * faz a impressão depender de internet (nem do CDN do Google, que registraria
 * quem imprime).
 *
 * A ordem da lista é a ordem em que os grupos aparecem na tela; quem agrupa é
 * `CATEGORIA_FONTE`, e `fontesPorCategoria()` monta os grupos já prontos.
 */
export const FONTES_NOMEADAS = [
    'SISTEMA',
    'PLAYFAIR_DISPLAY',
    'INTER',
    'MONTSERRAT',
    'CAVEAT',
    'DANCING_SCRIPT',
    'GREAT_VIBES',
    'NUNITO',
    'QUICKSAND',
    'COMFORTAA',
    'BALOO_2',
    'POPPINS',
    'OUTFIT',
    'MANROPE',
    'BARLOW_CONDENSED',
    'OSWALD',
    'PATRICK_HAND',
    'BEBAS_NEUE',
];
/** Agrupamento por clima: é assim que a lista aparece no assistente. */
export const CATEGORIAS_FONTE = [
    'SISTEMA',
    'SERIFADA',
    'NEUTRA',
    'MANUSCRITA',
    'ARREDONDADA',
    'GEOMETRICA',
    'CONDENSADA',
    'INFANTIL',
    'DISPLAY',
];
export const ROTULO_CATEGORIA_FONTE = {
    SISTEMA: 'Do aparelho',
    SERIFADA: 'Serifada',
    NEUTRA: 'Neutra e legível',
    MANUSCRITA: 'Manuscrita e caligráfica',
    ARREDONDADA: 'Arredondada e amigável',
    GEOMETRICA: 'Geométrica e moderna',
    CONDENSADA: 'Condensada (cabe mais)',
    INFANTIL: 'Infantil e escolar',
    DISPLAY: 'Display para título',
};
export const DESCRICAO_CATEGORIA_FONTE = {
    SISTEMA: 'A letra que já está no computador de quem imprime. Nenhum arquivo extra entra no PDF.',
    SERIFADA: 'Papelaria fina: presença e contraste entre traço grosso e fino.',
    NEUTRA: 'Sem serifa e sem enfeite: a mais discreta, feita para ler.',
    MANUSCRITA: 'Escrita à mão: é o que mais tira a agenda do jeito mecânico.',
    ARREDONDADA: 'Cantos macios e simpáticos: tira a cara de formulário.',
    GEOMETRICA: 'Formas circulares e limpas: moderna sem ser fria.',
    CONDENSADA: 'Estreita: cabe mais texto na mesma linha. Salva o A6 e os quadros apertados.',
    INFANTIL: 'Letra de escola, para a agenda da criança.',
    DISPLAY: 'Desenhada para título grande: pouca palavra e muito impacto.',
};
export const CATEGORIA_FONTE = {
    SISTEMA: 'SISTEMA',
    PLAYFAIR_DISPLAY: 'SERIFADA',
    INTER: 'NEUTRA',
    MONTSERRAT: 'GEOMETRICA',
    CAVEAT: 'MANUSCRITA',
    DANCING_SCRIPT: 'MANUSCRITA',
    GREAT_VIBES: 'MANUSCRITA',
    NUNITO: 'ARREDONDADA',
    QUICKSAND: 'ARREDONDADA',
    COMFORTAA: 'ARREDONDADA',
    BALOO_2: 'ARREDONDADA',
    POPPINS: 'GEOMETRICA',
    OUTFIT: 'GEOMETRICA',
    MANROPE: 'NEUTRA',
    BARLOW_CONDENSED: 'CONDENSADA',
    OSWALD: 'CONDENSADA',
    PATRICK_HAND: 'INFANTIL',
    BEBAS_NEUE: 'DISPLAY',
};
export const ROTULO_FONTE_NOMEADA = {
    SISTEMA: 'Do sistema',
    PLAYFAIR_DISPLAY: 'Playfair Display',
    INTER: 'Inter',
    MONTSERRAT: 'Montserrat',
    CAVEAT: 'Caveat',
    DANCING_SCRIPT: 'Dancing Script',
    GREAT_VIBES: 'Great Vibes',
    NUNITO: 'Nunito',
    QUICKSAND: 'Quicksand',
    COMFORTAA: 'Comfortaa',
    BALOO_2: 'Baloo 2',
    POPPINS: 'Poppins',
    OUTFIT: 'Outfit',
    MANROPE: 'Manrope',
    BARLOW_CONDENSED: 'Barlow Condensed',
    OSWALD: 'Oswald',
    PATRICK_HAND: 'Patrick Hand',
    BEBAS_NEUE: 'Bebas Neue',
};
export const DESCRICAO_FONTE_NOMEADA = {
    SISTEMA: 'Georgia e a sem serifa do aparelho. Nenhum arquivo extra entra no PDF.',
    PLAYFAIR_DISPLAY: 'Serifada de revista: títulos altos e elegantes, contraste forte entre traço grosso e fino.',
    INTER: 'Sem serifa desenhada para leitura em tela: neutra e a mais legível no texto miúdo.',
    MONTSERRAT: 'Sem serifa geométrica de cartaz: títulos largos e firmes. Em texto corrido ocupa mais largura — vale usar só nos títulos.',
    CAVEAT: 'Manuscrita de caneta, rápida e informal. Ótima no nome do mês e no título do dia; no texto miúdo cansa a vista.',
    DANCING_SCRIPT: 'Manuscrita inclinada, com brilho de convite. Rende em título — em texto corrido embola.',
    GREAT_VIBES: 'Caligráfica de festa, com laçadas largas. Só em título curto: a maiúscula dela ocupa muita largura.',
    NUNITO: 'Arredondada e macia, feita para leitura. É a manuscrita-friendly que também aguenta texto corrido.',
    QUICKSAND: 'Arredondada geométrica, leve e descontraída. Boa nos quadros e nas listas.',
    COMFORTAA: 'Arredondada larga, com cara de brinquedo. Bonita em título curto; no texto ocupa bastante largura.',
    BALOO_2: 'Arredondada gordinha, de cartaz infantil. Pede título curto e frase de efeito.',
    POPPINS: 'Geométrica limpa e circular. Funciona nos dois papéis, do título ao texto miúdo.',
    OUTFIT: 'Geométrica enxuta, de placa e vitrine. Elegante no título e estável no texto.',
    MANROPE: 'Neutra e sóbria: a mais discreta das geométricas, feita para blocos de texto.',
    BARLOW_CONDENSED: 'Condensada estreita de verdade: cabe muito texto na mesma linha — socorro no A6 e nos quadros apertados.',
    OSWALD: 'Condensada alta e firme, de jornal. Título com presença sem gastar largura.',
    PATRICK_HAND: 'Letra de mão infantil, de lápis. Feita para agenda escolar e de criança.',
    BEBAS_NEUE: 'Display de caixa alta, alto e estreito. Só título: a minúscula dela sai em versalete, então não serve para texto.',
};
/**
 * Quanto cada fonte é mais larga que a média que o motor estima, caractere a
 * caractere.
 *
 * A conta de altura (`layout/medidas.ts`) supõe meia altura de fonte por
 * caractere. Fonte geométrica larga ocupa mais que isso, e uma caixa
 * dimensionada por essa conta cortaria a última linha em silêncio — a mesma
 * armadilha do A6, com outra causa. O fator entra na estimativa, não no desenho.
 *
 * De onde vêm os números: medidos no Chromium, com os mesmos arquivos que o site
 * serve (`apps/web/medir-fontes.mjs`), sobre **duas** amostras — texto corrido e
 * maiúsculas de título — e vale o maior dos dois. É a amostra de maiúsculas que
 * manda: título é maiúsculo, e é nele que a fonte larga corta. Sobre o maior vai
 * uma folga de 3%, porque nenhuma amostra representa todo texto possível. Fonte
 * condensada sai com fator **abaixo** de 1 de propósito: ela realmente cabe mais
 * letra por linha, e o fator existe para o motor não reservar espaço à toa.
 *
 * As quatro primeiras (`SISTEMA`, `PLAYFAIR_DISPLAY`, `INTER`, `MONTSERRAT`) são
 * as que existiam antes, com os valores que sempre estiveram aqui: medi-las hoje
 * daria números maiores (Playfair 1,09, Inter 1,16, Montserrat 1,21, sistema
 * 1,20), mas trocá-los mudaria a conta de altura de **miolo já salvo** — a mesma
 * página sairia com menos linhas. Fica registrado, e o ajuste só se algum miolo
 * salvo aparecer cortado.
 */
export const FATOR_LARGURA_FONTE = {
    SISTEMA: 1,
    PLAYFAIR_DISPLAY: 1.04,
    INTER: 0.97,
    MONTSERRAT: 1.12,
    CAVEAT: 0.89,
    DANCING_SCRIPT: 0.98,
    GREAT_VIBES: 1.43,
    NUNITO: 1.14,
    QUICKSAND: 1.1,
    COMFORTAA: 1.22,
    BALOO_2: 0.98,
    POPPINS: 1.1,
    OUTFIT: 1.08,
    MANROPE: 1.06,
    BARLOW_CONDENSED: 0.75,
    OSWALD: 0.85,
    PATRICK_HAND: 0.85,
    BEBAS_NEUE: 0.69,
};
/**
 * As fontes agrupadas por clima, prontas para a tela.
 *
 * Grupo vazio não entra: quem esconde um grupo é a própria lista, e não a tela.
 */
export function fontesPorCategoria() {
    return CATEGORIAS_FONTE.map((categoria) => ({
        categoria,
        rotulo: ROTULO_CATEGORIA_FONTE[categoria],
        descricao: DESCRICAO_CATEGORIA_FONTE[categoria],
        fontes: FONTES_NOMEADAS.filter((fonte) => CATEGORIA_FONTE[fonte] === categoria),
    })).filter((grupo) => grupo.fontes.length > 0);
}
/* ------------------------------------------------------------------ */
/* Ornamentos                                                          */
/* ------------------------------------------------------------------ */
export const ORNAMENTOS = [
    'NENHUM',
    'LINHA_FINA',
    'CANTO',
    'ARCO',
    'FLORAL',
    'GEOMETRICO',
    'PONTOS',
    'QUADRICULADO',
    'ONDAS',
    'CONFETE',
];
export const ROTULO_ORNAMENTO = {
    NENHUM: 'Sem ornamento',
    LINHA_FINA: 'Filete fino',
    CANTO: 'Cantoneiras',
    ARCO: 'Arco suave',
    FLORAL: 'Flora / folhagem',
    GEOMETRICO: 'Geométrico',
    PONTOS: 'Papel pontilhado',
    QUADRICULADO: 'Quadriculado leve',
    ONDAS: 'Ondinhas',
    CONFETE: 'Confete',
};
/**
 * Os ornamentos que decoram **a folha inteira**, e não um canto dela.
 *
 * São padrões repetidos por baixo da escrita — o papel pontilhado do caderno,
 * o quadriculado do caderno de desenho, ondinhas e confete. Como são pintados no
 * fundo (não ocupam espaço no fluxo), **não cortam conteúdo**: quem escolhe um
 * deles não muda nada além do desenho. Ficam separados para o assistente poder
 * explicar a diferença e para o desenho saber quando repetir em vez de posicionar.
 */
export const ORNAMENTOS_DE_PAPEL = ['PONTOS', 'QUADRICULADO', 'ONDAS', 'CONFETE'];
/** Este ornamento é um padrão de folha inteira? */
export function ehOrnamentoDePapel(ornamento) {
    return ORNAMENTOS_DE_PAPEL.includes(ornamento);
}
/**
 * A medida do padrão de papel, em milímetros: a primeira é a largura, a segunda
 * a altura da repetição. Texto de apoio do assistente e desenho leem daqui, para
 * não haver duas réguas.
 */
export const PASSO_ORNAMENTO_DE_PAPEL_MM = {
    PONTOS: [6, 6],
    QUADRICULADO: [5, 5],
    ONDAS: [14, 7],
    CONFETE: [22, 18],
};
/* ------------------------------------------------------------------ */
/* Imagem de fundo                                                     */
/* ------------------------------------------------------------------ */
export const ESCOPOS_IMAGEM = ['CAPA', 'CONTRACAPA', 'DIVISORIAS', 'DIAS', 'CADERNOS', 'CONTEUDO', 'TODAS'];
export const ROTULO_ESCOPO = {
    CAPA: 'Somente na capa',
    CONTRACAPA: 'Somente na contracapa',
    DIVISORIAS: 'Divisórias de mês',
    DIAS: 'Somente nas páginas de dia',
    CADERNOS: 'Somente nas páginas de caderno',
    CONTEUDO: 'Somente nas páginas de conteúdo',
    TODAS: 'Em todas as páginas',
};
export const AJUSTES_IMAGEM = ['COBRIR', 'CONTER', 'TOPO', 'ESQUERDA'];
export const ROTULO_AJUSTE = {
    COBRIR: 'Preencher a caixa (pode cortar)',
    CONTER: 'Caber inteira (pode sobrar)',
    TOPO: 'Alinhar ao topo',
    ESQUERDA: 'Alinhar à esquerda',
};
/* ------------------------------------------------------------------ */
/* Camadas de imagem                                                   */
/* ------------------------------------------------------------------ */
export const PAPEIS_IMAGEM = ['PAGINA_INTEIRA', 'BORDA', 'CANTO', 'LIVRE'];
export const ROTULO_PAPEL_IMAGEM = {
    PAGINA_INTEIRA: 'Página inteira',
    BORDA: 'Faixa de borda',
    CANTO: 'Canto da página',
    LIVRE: 'Posição livre',
};
export const DESCRICAO_PAPEL_IMAGEM = {
    PAGINA_INTEIRA: 'Cobre a página toda. Ideal para fundos, texturas e aquarelas.',
    BORDA: 'Encosta em uma das quatro bordas e acompanha toda a extensão dela.',
    CANTO: 'Fica ancorada em um dos quatro cantos, com a distância que você definir.',
    LIVRE: 'Você posiciona em qualquer ponto, com coordenadas livres em milímetros.',
};
export const LADOS = ['TOPO', 'BASE', 'ESQUERDA', 'DIREITA'];
export const ROTULO_LADO = {
    TOPO: 'Topo',
    BASE: 'Base',
    ESQUERDA: 'Esquerda',
    DIREITA: 'Direita',
};
export const CANTOS = ['SUP_ESQ', 'SUP_DIR', 'INF_DIR', 'INF_ESQ'];
export const ROTULO_CANTO = {
    SUP_ESQ: 'Superior esquerdo',
    SUP_DIR: 'Superior direito',
    INF_DIR: 'Inferior direito',
    INF_ESQ: 'Inferior esquerdo',
};
/** Símbolo de seta de cada canto, para o seletor visual. */
export const SIMBOLO_CANTO = {
    SUP_ESQ: '↖',
    SUP_DIR: '↗',
    INF_DIR: '↘',
    INF_ESQ: '↙',
};
/**
 * Como a camada se distribui entre as páginas em que o escopo se aplica.
 * - TODAS: aparece em todas elas.
 * - ALTERNADA: as camadas do mesmo grupo entram em rodízio, uma por página.
 * - A_CADA_N: cada camada ocupa N páginas seguidas; depois passa para a próxima.
 * - PAGINAS: só nas páginas listadas (ex.: "1,3,5-10").
 */
export const MODOS_RODIZIO = ['TODAS', 'ALTERNADA', 'A_CADA_N', 'PAGINAS'];
export const ROTULO_RODIZIO = {
    TODAS: 'Em todas as páginas do escopo',
    ALTERNADA: 'Alternando com as outras camadas deste papel',
    A_CADA_N: 'A cada N páginas, trocando entre as camadas deste papel',
    PAGINAS: 'Somente em páginas específicas',
};
/**
 * Máscaras impressas sobre as linhas dos dados do proprietário.
 * São guias para a caneta, não campos de formulário.
 */
export const MASCARAS_DADOS = {
    data: '__/__/______',
    telefone: '(00) 00000-0000',
    telefoneFixo: '(00) 0000-0000',
    cep: '00000-000',
    email: 'nome@exemplo.com',
};
/* ------------------------------------------------------------------ */
/* Páginas extras                                                      */
/* ------------------------------------------------------------------ */
export const PAGINAS_EXTRAS = [
    'DADOS_PESSOAIS',
    'ANO',
    'MESES',
    'ANIVERSARIOS',
    'METAS',
    'INDICE',
];
export const ROTULO_PAGINA_EXTRA = {
    DADOS_PESSOAIS: 'Meus dados',
    ANO: 'Visão do ano',
    MESES: 'Divisórias de mês com mini calendário',
    ANIVERSARIOS: 'Aniversários e datas especiais',
    METAS: 'Metas do período',
    INDICE: 'Índice',
};
export const DESCRICAO_PAGINA_EXTRA = {
    DADOS_PESSOAIS: 'Página com nome, contato e aviso de devolução em caso de perda.',
    ANO: 'Um panorama do ano com os 12 meses em miniaturas.',
    MESES: 'Cada mês abre com uma página de respiro e mini calendário.',
    ANIVERSARIOS: 'Tabela para registrar aniversários e datas importantes.',
    METAS: 'Espaço estruturado para metas e acompanhamento.',
    INDICE: 'Sumário com números de página por mês.',
};
/* ------------------------------------------------------------------ */
/* Cadernos                                                            */
/* ------------------------------------------------------------------ */
export const TIPOS_CADERNO = [
    'CONTATOS',
    'SENHAS',
    'EMERGENCIA',
    'ORACOES',
    'LEITURA_BIBLIA',
    'PAUTADO',
    'CALIGRAFIA',
    'PARTITURA',
    'DESENHO',
    'RECEITAS',
    'FINANCAS',
    'HABITOS',
    'TREINO',
    'ESTUDOS',
    'CONVIDADOS',
    'PLANEJAMENTO',
    'HORARIO',
    'REUNIOES',
    'PROJETOS',
    'PACIENTES',
    'ORCAMENTOS',
    'ESTOQUE',
    'PROMPTS',
    'FOLHAS',
];
export const ROTULO_CADERNO = {
    CONTATOS: 'Contatos',
    SENHAS: 'Senhas e acessos',
    EMERGENCIA: 'Emergência, alergias e contatos',
    ORACOES: 'Diário de orações',
    LEITURA_BIBLIA: 'Leitura da Bíblia em 365 dias',
    PAUTADO: 'Caderno pautado',
    CALIGRAFIA: 'Folhas de caligrafia',
    PARTITURA: 'Pauta musical (partitura)',
    DESENHO: 'Caderno de desenho',
    RECEITAS: 'Receitas',
    FINANCAS: 'Controle de gastos',
    HABITOS: 'Rastreador de hábitos',
    TREINO: 'Ficha de treino',
    ESTUDOS: 'Fichas de estudo',
    CONVIDADOS: 'Lista de convidados',
    PLANEJAMENTO: 'Checklist de tarefas',
    HORARIO: 'Horário escolar',
    REUNIOES: 'Reuniões e atas',
    PROJETOS: 'Painel de projetos',
    PACIENTES: 'Ficha de atendimento',
    ORCAMENTOS: 'Orçamentos',
    ESTOQUE: 'Estoque e materiais',
    PROMPTS: 'Biblioteca de prompts de IA',
    FOLHAS: 'Folhas para escrever',
};
export const DESCRICAO_CADERNO = {
    CONTATOS: 'Nome, e-mail, telefone e chave PIX. Você escolhe quantas páginas.',
    SENHAS: 'Nome da página ou serviço, endereço, usuário e senha. Lembre-se: senha impressa é senha exposta — guarde a agenda em lugar seguro.',
    EMERGENCIA: 'Ficha médica com alergias, tipo sanguíneo e plano de saúde, mais páginas de “a quem ligar”.',
    ORACOES: 'Cartões com data, pedido de oração e o registro de como foi respondido.',
    LEITURA_BIBLIA: 'Plano que cobre todos os capítulos da Bíblia em 365 dias, com caixinha para marcar cada dia. As páginas são calculadas automaticamente.',
    PAUTADO: 'Páginas de linhas livres, para receitas, atas, estudos ou qualquer anotação.',
    CALIGRAFIA: 'Folhas com guias de traçado para treinar letra cursiva ou de forma.',
    PARTITURA: 'Pentagramas em branco, como no papel de manuscrito, para compor, copiar ou estudar música.',
    DESENHO: 'Suas imagens viram as páginas do caderno. O restante sai em branco, com moldura.',
    RECEITAS: 'Ficha com ingredientes e modo de preparo, para as receitas de sempre.',
    FINANCAS: 'Registro de gastos com data, descrição, categoria e valor.',
    HABITOS: 'Grade do mês para marcar hábitos todos os dias.',
    TREINO: 'Ficha de treino com exercícios, séries, repetições e carga.',
    ESTUDOS: 'Resumo, dúvidas e próximos passos de cada sessão de estudo.',
    CONVIDADOS: 'Nome, contato, se confirmou e em que mesa senta. Já sai com a lista que você escrever aqui.',
    PLANEJAMENTO: 'Checklist de tarefas com prazo e caixinha. As tarefas que você escrever vêm impressas nas linhas.',
    HORARIO: 'Grade da semana para as aulas, mais uma tabela de matérias com professor e sala.',
    REUNIOES: 'Uma página por reunião: data, quem participou, pauta, decisões e o que ficou para fazer.',
    PROJETOS: 'Painel com projeto, responsável, prazo, situação e andamento — para enxergar tudo de uma vez.',
    PACIENTES: 'Ficha de atendimento com data, nome, queixa ou procedimento, evolução e retorno.',
    ORCAMENTOS: 'Proposta com cliente, item, valor, validade e situação, para acompanhar o que foi aprovado.',
    ESTOQUE: 'Item, quantidade, mínimo, validade e fornecedor. Bom para esmaltes, materiais e insumos.',
    PROMPTS: 'Guia de IA e de escrita de prompts, mais fichas para registrar os prompts que você testou e deram certo.',
    FOLHAS: 'Só folhas de escrita: você escolhe o estilo e quantas páginas de cada um (pautada, tracejada, pontilhada...).',
};
/** Tipos em que a quantidade de páginas é calculada pelo gerador. */
export const CADERNOS_AUTOMATICOS = ['LEITURA_BIBLIA', 'DESENHO'];
/* ---- Cânon bíblico do plano de leitura ---- */
export const CANONS_BIBLIA = ['EVANGELICA', 'CATOLICA'];
export const ROTULO_CANON = {
    EVANGELICA: 'Evangélica — 66 livros, 1.189 capítulos',
    CATOLICA: 'Católica — 73 livros, 1.334 capítulos',
};
export const DESCRICAO_CANON = {
    EVANGELICA: 'Cânon protestante, na ordem canônica habitual.',
    CATOLICA: 'Inclui os deuterocanônicos (Tobias, Judite, Sabedoria, Eclesiástico, Baruc, 1 e 2 Macabeus) e os acréscimos a Ester e Daniel.',
};
export const ESTILOS_CALIGRAFIA = ['PAUTA_SIMPLES', 'PAUTA_DUPLA', 'CURSIVA', 'QUADRICULADO'];
export const ROTULO_ESTILO_CALIGRAFIA = {
    PAUTA_SIMPLES: 'Pauta simples',
    PAUTA_DUPLA: 'Pauta dupla (linha e base)',
    CURSIVA: 'Cursiva com guia de altura',
    QUADRICULADO: 'Quadriculado',
};
/** Soma as páginas dos blocos de um caderno de folhas. */
export function totalDeFolhas(blocos) {
    return (blocos ?? []).reduce((total, bloco) => total + Math.max(1, Math.round(bloco.paginas)), 0);
}
/* ------------------------------------------------------------------ */
/* Quadros opcionais das páginas de dia                                */
/* ------------------------------------------------------------------ */
export const QUADROS_DIA = [
    'PALAVRA_DO_DIA',
    'FRASE_DO_DIA',
    /** Curiosidade do estilo CURIOSIDADES: o tema e o fato do dia. */
    'CURIOSIDADE_DO_DIA',
    /** O que observar ou testar hoje, do mesmo estilo. */
    'EXPERIMENTE_HOJE',
    /** Pergunta do dia do estilo PERGUNTAS: o tema, a pergunta e o porquê dela. */
    'PERGUNTA_DO_DIA',
    /** O passo de escrita de hoje, com as linhas para responder. */
    'DIARIO_GUIADO',
    /** Desafio do estilo ESTUDAR: o tema, o desafio e a pista de como chegar. */
    'DESAFIO_DO_DIA',
    /** A palavra nova do dia, com o significado e a linha para usar numa frase. */
    'PALAVRA_NOVA',
    'PRIORIDADES',
    'META_DO_DIA',
    'REVISAO_NOTURNA',
    'GRATIDAO',
    'ORACAO',
    'HABITOS',
    'CONTATOS_DO_DIA',
    'IDEIAS',
    'AGUA',
    'HUMOR',
    'TEMPO',
    'FINANCAS',
    'ANIVERSARIANTES',
    /**
     * Grade de horários do dia: uma linha por hora, com o início e o fim de cada
     * grupo de dias (segunda a sexta, sábado e domingo).
     */
    'GRADE_HORARIOS',
    /** Mini calendário do mês da página, com o dia destacado e os feriados. */
    'CALENDARIO_MES',
];
/** Onde o quadro fica na página. */
export const GRUPO_QUADRO = {
    PALAVRA_DO_DIA: 'LATERAL',
    FRASE_DO_DIA: 'LATERAL',
    CURIOSIDADE_DO_DIA: 'LATERAL',
    EXPERIMENTE_HOJE: 'LATERAL',
    PERGUNTA_DO_DIA: 'LATERAL',
    DIARIO_GUIADO: 'LATERAL',
    DESAFIO_DO_DIA: 'LATERAL',
    PALAVRA_NOVA: 'LATERAL',
    PRIORIDADES: 'LATERAL',
    META_DO_DIA: 'LATERAL',
    REVISAO_NOTURNA: 'LATERAL',
    GRATIDAO: 'LATERAL',
    ORACAO: 'LATERAL',
    HABITOS: 'LATERAL',
    CONTATOS_DO_DIA: 'LATERAL',
    IDEIAS: 'LATERAL',
    AGUA: 'FAIXA',
    HUMOR: 'FAIXA',
    TEMPO: 'FAIXA',
    FINANCAS: 'FAIXA',
    ANIVERSARIANTES: 'FAIXA',
    GRADE_HORARIOS: 'LATERAL',
    CALENDARIO_MES: 'LATERAL',
};
export const ROTULO_QUADRO = {
    PALAVRA_DO_DIA: 'Palavra do dia',
    FRASE_DO_DIA: 'Frase do dia',
    CURIOSIDADE_DO_DIA: 'Você sabia?',
    EXPERIMENTE_HOJE: 'Experimente hoje',
    PERGUNTA_DO_DIA: 'Pergunta do dia',
    DIARIO_GUIADO: 'Diário guiado',
    DESAFIO_DO_DIA: 'Desafio do dia',
    PALAVRA_NOVA: 'Palavra nova',
    PRIORIDADES: 'Prioridades do dia',
    META_DO_DIA: 'Meta do dia',
    REVISAO_NOTURNA: 'Revisão noturna',
    GRATIDAO: 'Gratidão',
    ORACAO: 'Oração',
    HABITOS: 'Hábitos',
    CONTATOS_DO_DIA: 'Falar com',
    IDEIAS: 'Ideias',
    AGUA: 'Água',
    HUMOR: 'Humor (1 a 5)',
    TEMPO: 'Tempo',
    FINANCAS: 'Gastos do dia',
    ANIVERSARIANTES: 'Aniversariantes',
    GRADE_HORARIOS: 'Grade de horários',
    CALENDARIO_MES: 'Mini calendário do mês',
};
/**
 * O desenho que cada quadro leva ao lado do título quando o miolo pede ícones.
 *
 * A escolha é por **assunto**, e não por estética: gota para água, cupcake para
 * aniversariantes, coração para gratidão, lua para a revisão noturna (a hora de
 * dormir), relógio para a grade de horários. O desenho sai na cor de destaque do
 * tema, então ele acompanha qualquer paleta.
 */
export const ICONE_QUADRO = {
    PALAVRA_DO_DIA: 'LIVRO',
    FRASE_DO_DIA: 'ASPAS',
    CURIOSIDADE_DO_DIA: 'INTERROGACAO',
    EXPERIMENTE_HOJE: 'OLHO',
    PERGUNTA_DO_DIA: 'ASPAS',
    DIARIO_GUIADO: 'LAPIS',
    DESAFIO_DO_DIA: 'ALVO',
    PALAVRA_NOVA: 'LIVRO',
    PRIORIDADES: 'PRANCHETA',
    META_DO_DIA: 'ALVO',
    REVISAO_NOTURNA: 'LUA',
    GRATIDAO: 'CORACAO',
    ORACAO: 'CRUZ',
    HABITOS: 'CHECK',
    CONTATOS_DO_DIA: 'TELEFONE',
    IDEIAS: 'LAMPADA',
    AGUA: 'GOTA',
    HUMOR: 'CARINHA',
    TEMPO: 'SOL',
    FINANCAS: 'MOEDA',
    ANIVERSARIANTES: 'CUPCAKE',
    GRADE_HORARIOS: 'RELOGIO',
    CALENDARIO_MES: 'CALENDARIO',
};
export const DESCRICAO_QUADRO = {
    PALAVRA_DO_DIA: 'Referência bíblica, reflexão curta e oração sugerida.',
    FRASE_DO_DIA: 'Frase motivacional com uma ação prática.',
    CURIOSIDADE_DO_DIA: 'Um fato do mundo por dia, com o porquê dele por dentro.',
    EXPERIMENTE_HOJE: 'Uma coisa pequena para observar, testar ou contar hoje.',
    PERGUNTA_DO_DIA: 'Uma pergunta para pensar, com o porquê dela logo abaixo.',
    DIARIO_GUIADO: 'O passo de escrita do dia, com as linhas para responder.',
    DESAFIO_DO_DIA: 'Um desafio curto para fazer junto, com a pista de como chegar lá.',
    PALAVRA_NOVA: 'A palavra do dia, o que ela quer dizer e a linha para usar numa frase.',
    PRIORIDADES: 'Três linhas com caixinha, para o essencial do dia.',
    META_DO_DIA: 'O que fazer e como fazer, em dois campos.',
    REVISAO_NOTURNA: 'Área de escrita que cresce para ocupar a sobra da página.',
    GRATIDAO: 'Três motivos com caixinha, para fechar o dia bem.',
    ORACAO: 'Linhas livres para o seu momento de oração.',
    HABITOS: 'Cinco linhas para acompanhar hábitos diários.',
    CONTATOS_DO_DIA: 'Quem você precisa procurar hoje.',
    IDEIAS: 'Captura rápida de ideias soltas.',
    AGUA: 'Oito marcas para acompanhar a hidratação.',
    HUMOR: 'Escala de 1 a 5 para registrar como o dia foi.',
    TEMPO: 'Temperatura mínima e máxima.',
    FINANCAS: 'Entradas e saídas do dia.',
    ANIVERSARIANTES: 'Quem faz aniversário nesta data.',
    CALENDARIO_MES: 'O mês inteiro em grade, com o dia de hoje destacado e os feriados marcados.',
    GRADE_HORARIOS: 'A coluna de horas do dia, com o horário escolhido para cada grupo de dias. O dia marcado como livre sai só com linhas.',
};
/** Os três grupos de dias que a grade sabe tratar separadamente. */
export const GRUPOS_HORARIO = ['SEG_A_SEX', 'SABADO', 'DOMINGO'];
export const ROTULO_GRUPO_HORARIO = {
    SEG_A_SEX: 'Segunda a sexta',
    SABADO: 'Sábado',
    DOMINGO: 'Domingo',
};
/** O nome do campo de cada grupo na configuração. */
export const CHAVE_DO_GRUPO_HORARIO = {
    SEG_A_SEX: 'segAsex',
    SABADO: 'sabado',
    DOMINGO: 'domingo',
};
/** Passos de grade aceitos, em minutos. */
export const INTERVALOS_HORARIO = [60, 30];
/** Rótulo do quadro quando o grupo de dias está marcado como livre. */
export const ROTULO_DIA_LIVRE = 'Dia livre';
/** Horas oferecidas nos seletores, de meia em meia hora. */
export const HORAS_DO_DIA = Array.from({ length: 48 }, (_, indice) => {
    const hora = String(Math.floor(indice / 2)).padStart(2, '0');
    const minuto = indice % 2 === 0 ? '00' : '30';
    return `${hora}:${minuto}`;
});
/** Grade recém-criada: dia corrido das 7h às 19h, de hora em hora, nenhum dia livre. */
export function criarGradeHorariosPadrao() {
    const corrido = { inicio: '07:00', fim: '19:00', livre: false };
    return {
        segAsex: { ...corrido },
        sabado: { ...corrido },
        domingo: { ...corrido },
        intervaloMinutos: 60,
    };
}
/** Quadros sugeridos para cada estilo de conteúdo. */
export const QUADROS_RECOMENDADOS = {
    DEVOCIONAL: ['PALAVRA_DO_DIA', 'ORACAO', 'GRATIDAO', 'REVISAO_NOTURNA', 'AGUA', 'HUMOR'],
    MOTIVACIONAL: ['FRASE_DO_DIA', 'PRIORIDADES', 'META_DO_DIA', 'REVISAO_NOTURNA', 'AGUA', 'HUMOR'],
    /*
     * Curiosidades é a única sugestão com os **dois** quadros do próprio estilo: o
     * fato do dia e a coisa para observar. Prioridades e Ideias ficam de fora de
     * propósito — os dois quadros de conteúdo já ocupam boa parte da coluna (44 e
     * 34 mm), e a primeira versão desta sugestão, com Ideias junto, fez o motor
     * omitir um quadro por falta de espaço em A5 com um dia por página.
     */
    CURIOSIDADES: ['CURIOSIDADE_DO_DIA', 'EXPERIMENTE_HOJE', 'REVISAO_NOTURNA', 'AGUA', 'HUMOR'],
    /*
     * O par de perguntas mais a revisão e o humor. Fica enxuto pelo mesmo motivo
     * do estilo de curiosidades: dois quadros de conteúdo já ocupam boa parte da
     * coluna lateral, e o motor avisa quando a conta não fecha.
     */
    PERGUNTAS: ['PERGUNTA_DO_DIA', 'DIARIO_GUIADO', 'REVISAO_NOTURNA', 'HUMOR'],
    /*
     * O par de estudar mais o humor. Enxuto pelo mesmo motivo dos outros dois
     * estilos de conteúdo: dois quadros de conteúdo já ocupam boa parte da coluna
     * lateral, e o motor avisa quando a conta não fecha.
     */
    ESTUDAR: ['DESAFIO_DO_DIA', 'PALAVRA_NOVA', 'HUMOR'],
    SEM_ESTILO: ['PRIORIDADES', 'REVISAO_NOTURNA', 'AGUA'],
};
export function quadrosSugeridos(estilo) {
    const ligados = new Set(QUADROS_RECOMENDADOS[estilo]);
    return Object.fromEntries(QUADROS_DIA.map((quadro) => [quadro, ligados.has(quadro)]));
}
/**
 * Nenhum quadro ligado.
 *
 * É o padrão de fábrica do miolo novo (decisão de 27/09/2026): quem decide o que
 * aparece na página do dia é quem usa o assistente. As sugestões por estilo
 * continuam existindo — como **botão** no passo "Quadros do dia", e não como
 * algo já marcado. Ausente na configuração é o miolo que já existia: miolo
 * salvo com quadros guarda os quadros dele e não passa por aqui.
 */
export function quadrosDesligados() {
    return Object.fromEntries(QUADROS_DIA.map((quadro) => [quadro, false]));
}
export function quadrosLigados(quadros, grupo) {
    return QUADROS_DIA.filter((quadro) => quadros[quadro] && (!grupo || GRUPO_QUADRO[quadro] === grupo));
}
/* ------------------------------------------------------------------ */
/* Posição livre das caixas do dia                                     */
/* ------------------------------------------------------------------ */
/**
 * As caixas do dia que o usuário pode posicionar à mão.
 *
 * São os **quadros** (os da coluna e os do pé da página) e a **área de escrita**:
 * é o que se arrasta numa agenda de dia. O cabeçalho, o conteúdo do dia e as
 * divisórias continuam no lugar que o motor calcula — eles são a espinha da
 * página, e movê-los desmontaria a leitura.
 *
 * A chave da caixa é o próprio nome do quadro: estável, legível na configuração
 * e indiferente à ordem em que os quadros foram ligados. Índice de posição não
 * serviria: basta desligar um quadro para o número mudar de dono.
 */
export const CAIXAS_MOVEIS = ['ESCRITA', ...QUADROS_DIA];
export const ROTULO_CAIXA_MOVEL = {
    ESCRITA: 'Área de escrita',
    ...ROTULO_QUADRO,
};
//# sourceMappingURL=planner.js.map